﻿package org.example.project.engineslot

import androidx.compose.runtime.Composable
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private fun slotBookingArtText(booking: All): String =
    when (slotBookingArtCode(booking)) {
        SLOT_BEREICH_WA -> SLOT_BEREICH_WA
        SLOT_BEREICH_WE -> SLOT_BEREICH_WE
        else -> ""
    }

private fun slotBookingCompanyName(value: String): String =
    value.substringBefore(" · ").trim().ifBlank {
        value.substringBefore(" · ").trim().ifBlank { value.trim() }
    }

private fun slotBookingEntries(booking: All): List<All> =
    when (slotBookingArtCode(booking)) {
        SLOT_BEREICH_WE -> slotLiefernBelege(booking)
        SLOT_BEREICH_WA -> slotLadenTouren(booking)
        else -> listOf(booking)
    }

private fun slotBookingEntryHasContent(entry: All, art: String): Boolean =
    when (art) {
        SLOT_BEREICH_WE -> entry.lieferscheinnummer.isNotBlank() ||
            entry.palettenanzahl > 0 ||
            entry.unternehmer.isNotBlank()
        SLOT_BEREICH_WA -> entry.lieferscheinnummer.isNotBlank() ||
            entry.unternehmer.isNotBlank()
        else -> slotHasBookingData(entry)
    }

private fun slotBookingItemCount(booking: All): Int {
    val art = slotBookingArtCode(booking)
    val count = slotBookingEntries(booking).count { entry -> slotBookingEntryHasContent(entry, art) }
    return count.coerceAtLeast(1)
}

fun slotBookingSammelCount(booking: All): Int =
    if (slotBookingArtCode(booking) == SLOT_BEREICH_WA) slotBookingItemCount(booking) else 1

fun slotBookingIsSammelWa(booking: All): Boolean =
    slotBookingSammelCount(booking) > 1

private fun slotBookingPrimaryRefLabel(booking: All): String =
    if (slotBookingArtCode(booking) == SLOT_BEREICH_WA) "lieferscheinnummer" else "lieferscheinnummer"

private fun slotBookingPrimaryRefValue(booking: All): String {
    val art = slotBookingArtCode(booking)
    val values = slotBookingEntries(booking)
        .mapNotNull { entry ->
            when (art) {
                SLOT_BEREICH_WA -> entry.lieferscheinnummer.trim()
                SLOT_BEREICH_WE -> entry.lieferscheinnummer.trim()
                else -> entry.lieferscheinnummer.ifBlank { entry.lieferscheinnummer }.trim()
            }.takeIf { it.isNotBlank() }
        }
        .distinct()
    return when {
        values.isEmpty() -> ""
        slotBookingArtCode(booking) == SLOT_BEREICH_WA && values.size > 1 -> values.joinToString(" | ")
        values.size == 1 -> values.first()
        else -> "${values.first()} +${values.size - 1}"
    }
}

fun slotBookingYardStatusValue(booking: All): String {
    val slotStates = ZustandManager.Slot.allCodes().toSet()
    if (booking.zustand > 0 && booking.zustand !in slotStates) {
        return ZustandManager.textFor(booking.zustand)
    }
    val states = (listOf(booking.zustand) + slotBookingEntries(booking).map { it.zustand })
        .filter { it > 0 && it !in slotStates }
        .distinct()
    return when {
        states.isEmpty() -> ""
        states.size == 1 -> ZustandManager.textFor(states.first())
        else -> "${ZustandManager.textFor(states.first())} +${states.size - 1}"
    }
}

private fun slotBookingPalettenValue(booking: All): String {
    if (slotBookingArtCode(booking) == SLOT_BEREICH_WA) return ""
    val total = slotBookingEntries(booking).sumOf { it.palettenanzahl.coerceAtLeast(0) }
    return total.takeIf { it > 0 }?.toString().orEmpty()
}

private fun slotBookingUnternehmerValue(booking: All): String {
    val values = slotBookingEntries(booking)
        .map { entry -> slotBookingCompanyName(entry.unternehmer) }
        .filter { it.isNotBlank() }
        .distinct()
    return when {
        values.isEmpty() -> ""
        values.size == 1 -> values.first()
        else -> "${values.first()} +${values.size - 1}"
    }
}

private fun slotBookingLieferantValue(booking: All): String {
    if (slotBookingArtCode(booking) != SLOT_BEREICH_WE) return ""
    val values = slotBookingEntries(booking)
        .map { entry -> slotBookingCompanyName(entry.lieferant) }
        .filter { it.isNotBlank() }
        .distinct()
    return when {
        values.isEmpty() -> ""
        values.size == 1 -> values.first()
        else -> "${values.first()} +${values.size - 1}"
    }
}

private fun slotBookingEmpfaengerDisplay(booking: All): String =
    if (slotBookingArtCode(booking) == SLOT_BEREICH_WE) {
        ""
    } else {
        slotBookingEntries(booking)
            .map { entry -> slotBookingCompanyName(entry.empfaenger) }
            .filter { it.isNotBlank() }
            .distinct()
            .let { values ->
                if (slotBookingIsSammelWa(booking) && values.size > 1) "${values.first()} +${values.size - 1}"
                else values.joinToString(", ")
            }
    }

private fun slotBookingOrtValue(booking: All): String =
    slotBookingEntries(booking)
        .map { entry -> entry.ort.trim() }
        .filter { it.isNotBlank() }
        .distinct()
        .joinToString(", ")

@Composable
private fun SlotCompactHeaderLine(art: String, paletten: String, count: Int) {
    if (art.isBlank()) return
    Div({ classes(SlotStyles.bookingHeaderRow) }) {
        SlotCompactHeaderStat(
            icon = "images/lkw.svg",
            text = art,
            align = "start"
        )
        if (paletten.isNotBlank()) {
            SlotCompactHeaderStat(
                icon = "images/paletten.svg",
                text = paletten,
                align = "center"
            )
        }
        SlotCompactHeaderStat(
            icon = "images/order.svg",
            text = count.toString(),
            align = "end"
        )
    }
}

@Composable
private fun SlotCompactHeaderStat(icon: String, text: String, align: String) {
    Div({
        classes(SlotStyles.bookingHeaderStat)
        when (align) {
            "start" -> classes(SlotStyles.bookingHeaderStatStart)
            "center" -> classes(SlotStyles.bookingHeaderStatCenter)
            "end" -> classes(SlotStyles.bookingHeaderStatEnd)
        }
    }) {
        Img(src = icon, attrs = {
            classes(SlotStyles.bookingHeaderIcon)
        })
        Span({ classes(SlotStyles.bookingHeaderMeta) }) { Text(text) }
    }
}

@Composable
fun SlotBookingCardContent(booking: All) {
    val cellState = slotCellState(booking)
    if (cellState == org.example.project.service.ZustandManager.Slot.FREI) return
    if (cellState == org.example.project.service.ZustandManager.Slot.IN_BUCHUNG) {
        slotBookingArtText(booking).takeIf { it.isNotBlank() }?.let { art ->
            Span({ classes(SlotStyles.label) }) { Text(art) }
        }
        return
    }
    if (cellState == org.example.project.service.ZustandManager.Slot.GESPERRT) return

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.FlexStart)
            gap(4.px)
            minWidth(0.px)
            width(100.percent)
        }
    }) {
        SlotCompactHeaderLine(
            art = slotBookingArtText(booking),
            paletten = slotBookingPalettenValue(booking),
            count = slotBookingItemCount(booking)
        )
        Div({ classes(SlotStyles.bookingHeaderDividerLine) })
        SlotCompactTopDetailsRow(
            leftIcon = "images/order.svg",
            leftValue = slotBookingPrimaryRefValue(booking),
            rightIcon = "images/check_circle.svg",
            rightValue = slotBookingYardStatusValue(booking)
        )
        SlotCompactSummaryRow(
            icon = "images/lkw.svg",
            value = slotBookingUnternehmerValue(booking)
        )
        SlotCompactSummaryRow(
            icon = "images/domain.svg",
            value = slotBookingLieferantValue(booking)
        )
        SlotCompactSummaryRow(
            icon = "images/zielort.svg",
            value = listOf(
                slotBookingEmpfaengerDisplay(booking),
                slotBookingOrtValue(booking)
            ).filter { it.isNotBlank() }.joinToString(" ")
        )
    }
}

@Composable
private fun SlotCompactSummaryRow(icon: String, value: String) {
    if (value.isBlank()) return
    Div({ classes(SlotStyles.bookingSummaryRow) }) {
        Div({ classes(SlotStyles.bookingSummaryIconCell) }) {
            Img(src = icon, attrs = {
                classes(SlotStyles.bookingSummaryIcon)
            })
        }
        Span({ classes(SlotStyles.bookingSummaryValue) }) { Text(value) }
    }
}

@Composable
private fun SlotCompactTopDetailsRow(
    leftIcon: String,
    leftValue: String,
    rightIcon: String,
    rightValue: String
) {
    if (leftValue.isBlank()) return
    Div({ classes(SlotStyles.bookingTopDetailsRow) }) {
        Div({ classes(SlotStyles.bookingTopDetailsCell) }) {
            Img(src = leftIcon, attrs = {
                classes(SlotStyles.bookingSummaryIcon)
            })
            Span({ classes(SlotStyles.bookingSummaryValue) }) { Text(leftValue) }
        }
        if (rightValue.isNotBlank()) {
            Div({
                classes(SlotStyles.bookingTopDetailsCell)
                classes(SlotStyles.bookingTopDetailsCellRight)
            }) {
                Img(src = rightIcon, attrs = {
                    classes(SlotStyles.bookingSummaryIcon)
                })
                Span({ classes(SlotStyles.bookingSummaryValue) }) { Text(rightValue) }
            }
        }
    }
}

